import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import socket from "./socket";
import { api } from "./apiClient";
import { AuthContext } from "../context/AuthContext";

const SocketContext = createContext(null);

// status: "connecting" | "connected" | "disconnected" | "error"
export const SocketProvider = ({ storeId, children }) => {
  const { token, setToken, logout } = useContext(AuthContext);
  const [status, setStatus] = useState(
    socket.connected ? "connected" : "disconnected",
  );
  const [error, setError] = useState("");

  // Refs keep the connection effect from restarting when these change
  const tokenRef = useRef(token);
  const setTokenRef = useRef(setToken);
  const logoutRef = useRef(logout);
  const refreshingRef = useRef(false);
  const refreshedRef = useRef(false); // allow only one refresh per attempt
  tokenRef.current = token;
  setTokenRef.current = setToken;
  logoutRef.current = logout;

  const hasToken = Boolean(token);

  const joinStore = useCallback((id) => {
    if (!id || !socket.connected) return;
    socket.emit("join-store", id, (res) => {
      if (res?.error) {
        setError(
          res.error === "Forbidden"
            ? "You don't have access to this store."
            : res.error,
        );
      } else {
        setError("");
      }
    });
  }, []);

  // Connection lifecycle: depends only on being logged in, not on token value
  useEffect(() => {
    if (!hasToken) {
      socket.disconnect();
      setStatus("disconnected");
      return;
    }

    // Always send the newest token, including after a refresh
    socket.auth = (cb) => cb({ token: tokenRef.current });

    const failSession = (msg) => {
      setStatus("error");
      setError(msg);
      logoutRef.current?.();
    };

    const recover = async () => {
      if (refreshingRef.current) return;
      if (refreshedRef.current) {
        return failSession("Session expired. Please log in again.");
      }
      refreshingRef.current = true;
      refreshedRef.current = true;
      try {
        const res = await api.post("/auths/refresh");
        const newToken = res.data.accessToken;
        localStorage.setItem("token", newToken);
        tokenRef.current = newToken;
        setTokenRef.current?.(newToken);
        socket.connect();
      } catch {
        failSession("Session expired. Please log in again.");
      } finally {
        refreshingRef.current = false;
      }
    };

    const onConnect = () => {
      refreshedRef.current = false;
      setStatus("connected");
      setError("");
    };

    const onDisconnect = (reason) => {
      setStatus("disconnected");
      if (reason === "io server disconnect") recover(); // server closed it
    };

    const onConnectError = (err) => {
      const msg = err?.message || "";
      if (msg === "jwt expired") return recover();
      if (msg === "invalid token" || msg.startsWith("Authentication error")) {
        return failSession("Your session is not valid. Please log in again.");
      }
      // Server down or network problem: Socket.IO keeps retrying by itself
      setStatus("error");
      setError("Cannot reach the server. Retrying…");
    };

    const onReconnectFailed = () => {
      setStatus("error");
      setError("Server is not reachable. Check your connection and retry.");
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.io.on("reconnect_failed", onReconnectFailed);

    if (!socket.connected) {
      setStatus("connecting");
      socket.connect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.io.off("reconnect_failed", onReconnectFailed);
      socket.disconnect();
    };
  }, [hasToken]);

  // Join the store room (again after every reconnect), leave on change
  useEffect(() => {
    if (!storeId || status !== "connected") return;
    joinStore(storeId);
    return () => {
      if (socket.connected) socket.emit("leave-store", storeId);
    };
  }, [storeId, status, joinStore]);

  const retry = useCallback(() => {
    refreshedRef.current = false;
    setError("");
    setStatus("connecting");
    socket.connect();
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected: status === "connected",
        status,
        error,
        retry,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);

// Drop this anywhere (e.g. top of the dashboard) to show problems with a Retry button
export const SocketStatusBanner = () => {
  const { status, error, retry } = useSocket() || {};
  if (status !== "error" && !error) return null;
  return (
    <div
      className="alert alert-danger d-flex justify-content-between align-items-center py-2 mb-2"
      role="alert"
    >
      <span>{error || "Connection problem."}</span>
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        onClick={retry}
      >
        Retry
      </button>
    </div>
  );
};
